import {useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {PATHS} from 'routes/constants';
import {LAYOUT_MODE_KEY} from 'utils/constants';
import {SHORT_LINK_TYPE} from '@chekin/core';
import {useKioskoConnectionStatus} from 'context/kiosko/hooks/useKioskoConnectionStatus';

const getHousingCheckinOnlineInitPatch = (token = '', layout: string | null) => {
  const path =
    layout === 'kiosko'
      ? `${token}/${PATHS.searchReservation.welcomeScreen}`
      : `${token}/${PATHS.searchReservation.main}`;
  return path;
};

const getDirectReservationPath = (token = '', search = '') => {
  return `${token}/${PATHS.searchReservation.directReservation}${search}`;
};

const getUrlPathsFromShortLink = (
  type = '',
  token = '',
  layout: string | null,
  search = '',
) => {
  return {
    [SHORT_LINK_TYPE.main]: '',
    [SHORT_LINK_TYPE.payment]: `${token}/${PATHS.payments.main}`,
    [SHORT_LINK_TYPE.upselling]: `${token}/${PATHS.recommendations.main}`,
    [SHORT_LINK_TYPE.lock]: `${token}/${PATHS.remoteAccess}`,
    [SHORT_LINK_TYPE.ivHousing]: `${token}/${PATHS.verifyOnHousing}`,
    [SHORT_LINK_TYPE.housingCheckinOnline]: getHousingCheckinOnlineInitPatch(
      token,
      layout,
    ),
    [SHORT_LINK_TYPE.housingGuidebook]: `${token}/${PATHS.housingGuidebooks}`,
    [SHORT_LINK_TYPE.housingDirectReservation]: getDirectReservationPath(token, search),
  }[type];
};

const getLayoutMode = (isKioskoAttempted: boolean, isKioskoConnected: boolean) => {
  const storedLayout = localStorage.getItem(LAYOUT_MODE_KEY);
  return storedLayout === 'kiosko' || (isKioskoAttempted && isKioskoConnected)
    ? 'kiosko'
    : 'default';
};

type UseRedirectToUrlFromShortLinkProps = {
  linkType?: string;
  token?: string;
};

function useRedirectToUrlFromShortLink({
  linkType,
  token,
}: UseRedirectToUrlFromShortLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasBeenRedirected = useRef(false);
  const {
    isConnected: isKioskoConnected,
    hasCompletedInitialization,
    isAttempted: isKioskoAttempted,
  } = useKioskoConnectionStatus();

  const layoutMode = getLayoutMode(isKioskoAttempted, isKioskoConnected);

  useEffect(
    function redirectToUrlFromShortLink() {
      if (hasBeenRedirected.current) return;
      if (isKioskoAttempted && !hasCompletedInitialization) return;

      const redirectUrl = getUrlPathsFromShortLink(
        linkType,
        token,
        layoutMode,
        location.search,
      );
      if (redirectUrl) {
        navigate(redirectUrl, {replace: true});
        hasBeenRedirected.current = true;
      }
    },
    [
      token,
      linkType,
      navigate,
      layoutMode,
      location.pathname,
      location.search,
      isKioskoAttempted,
      hasCompletedInitialization,
    ],
  );
}

export {useRedirectToUrlFromShortLink};
